class RedditService {
  constructor() {
    this.name = 'Reddit Social Feeds';
    this.enabled = false; // User-controlled
    this.baseUrl = 'https://www.reddit.com';
    this.apiUrl = 'https://www.reddit.com/r';
    this.userAgent = 'BullBearly/1.0';
  }

  // Set user configuration
  setConfig(config) {
    this.enabled = config.enabled || false;
    this.apiKey = config.apiKey || null; // Optional for basic access
    this.rateLimit = config.rateLimit || 100; // requests per minute
  }

  // Get Reddit posts from a specific subreddit
  async getSubredditPosts(subreddit, options = {}) {
    try {
      if (!this.enabled) {
        console.log('RedditService: Service is disabled');
        return [];
      }

      console.log(`RedditService: Fetching posts from r/${subreddit}`);
      
      const sort = options.sort || 'hot'; // hot, new, top, rising
      const limit = options.limit || 25;
      const time = options.time || 'day'; // hour, day, week, month, year, all
      
      const url = `${this.apiUrl}/${subreddit}/${sort}.json?limit=${limit}&t=${time}`;
      
      console.log(`RedditService: Fetching from: ${url}`);
      
      const response = await fetch(url, {
        headers: {
          'User-Agent': this.userAgent
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (!data || !data.data || !data.data.children) {
        console.log(`RedditService: No posts found for r/${subreddit}`);
        return [];
      }
      
      const posts = data.data.children.map(child => this.formatRedditPost(child.data));
      
      console.log(`RedditService: Successfully fetched ${posts.length} posts from r/${subreddit}`);
      return posts;
    } catch (error) {
      console.error(`RedditService: Error fetching posts from r/${subreddit}:`, error);
      return [];
    }
  }

  // Search Reddit globally
  async searchReddit(query, options = {}) {
    try {
      if (!this.enabled) {
        console.log('RedditService: Service is disabled');
        return [];
      }

      console.log(`RedditService: Searching Reddit for "${query}"`);
      
      const sort = options.sort || 'relevance'; // relevance, hot, top, new, comments
      const limit = options.limit || 25;
      const time = options.time || 'day';
      const subreddit = options.subreddit || null;
      
          let url;
          if (subreddit) {
            url = `${this.baseUrl}/r/${subreddit}/search.json?q=${encodeURIComponent(query)}&sort=${sort}&limit=${limit}&t=${time}&restrict_sr=1`;
          } else {
            url = `${this.baseUrl}/search.json?q=${encodeURIComponent(query)}&sort=${sort}&limit=${limit}&t=${time}`;
          }
          
          console.log(`RedditService: Searching from: ${url}`);
          
          const response = await fetch(url, {
            headers: {
              'User-Agent': this.userAgent
            }
          });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (!data || !data.data || !data.data.children) {
        console.log(`RedditService: No results found for "${query}"`);
        return [];
      }
      
      const posts = data.data.children.map(child => this.formatRedditPost(child.data));
      
      console.log(`RedditService: Successfully found ${posts.length} posts for "${query}"`);
      return posts;
    } catch (error) {
      console.error(`RedditService: Error searching Reddit for "${query}":`, error);
      return [];
    }
  }

  // Format Reddit post data
  formatRedditPost(postData) {
    return {
      id: postData.id,
      title: postData.title,
      content: postData.selftext || '',
      author: postData.author,
      subreddit: postData.subreddit,
      url: `https://reddit.com${postData.permalink}`,
      externalUrl: postData.url,
      score: postData.score,
      upvoteRatio: postData.upvote_ratio,
      numComments: postData.num_comments,
      created: new Date(postData.created_utc * 1000).toISOString(),
      thumbnail: postData.thumbnail,
      isVideo: postData.is_video,
      isSelf: postData.is_self,
      domain: postData.domain,
      flair: postData.link_flair_text,
      gilded: postData.gilded,
      over18: postData.over_18,
      spoiler: postData.spoiler,
      stickied: postData.stickied,
      locked: postData.locked,
      archived: postData.archived,
      source: 'Reddit',
      sourceUrl: `https://reddit.com/r/${postData.subreddit}`,
      sourceName: `r/${postData.subreddit}`
    };
  }

  // Get posts from multiple subreddits
  async getMultipleSubredditPosts(subreddits, options = {}) {
    try {
      console.log(`RedditService: Fetching posts from ${subreddits.length} subreddits`);
      
      const promises = subreddits.map(subreddit => this.getSubredditPosts(subreddit, options));
      const results = await Promise.all(promises);
      
      // Flatten and sort by score
      const allPosts = results.flat().sort((a, b) => b.score - a.score);
      
      console.log(`RedditService: Successfully fetched ${allPosts.length} total posts`);
      return allPosts;
    } catch (error) {
      console.error('RedditService: Error fetching multiple subreddit posts:', error);
      return [];
    }
  }

  // Search multiple queries
  async searchMultipleQueries(queries, options = {}) {
    try {
      console.log(`RedditService: Searching for ${queries.length} queries`);
      
      const promises = queries.map(query => this.searchReddit(query, options));
      const results = await Promise.all(promises);
      
      // Flatten and sort by score
      const allPosts = results.flat().sort((a, b) => b.score - a.score);
      
      console.log(`RedditService: Successfully found ${allPosts.length} total posts`);
      return allPosts;
    } catch (error) {
      console.error('RedditService: Error searching multiple queries:', error);
      return [];
    }
  }

  // Check if service is available
  async isAvailable() {
    try {
      // Try to fetch a simple post to test connectivity
      const testPosts = await this.getSubredditPosts('test', { limit: 1 });
      return testPosts.length >= 0; // Even 0 posts means the API is working
    } catch (error) {
      console.error('RedditService: Service not available:', error);
      return false;
    }
  }

  // Get service info
  getServiceInfo() {
    return {
      name: this.name,
      enabled: this.enabled,
      description: 'Reddit social media integration for financial discussions',
      features: [
        'Browse subreddit posts',
        'Global Reddit search',
        'Multiple subreddit support',
        'Sort by hot, new, top, rising',
        'No API key required for basic access',
        'Rate limiting protection'
      ],
      limitations: [
        '100 requests per minute limit',
        'No authentication features',
        'Limited to public posts only',
        'No user-specific content'
      ],
      popularSubreddits: [
        'stocks',
        'investing',
        'SecurityAnalysis',
        'ValueInvesting',
        'StockMarket',
        'dividends',
        'options',
        'wallstreetbets',
        'SecurityAnalysis',
        'ValueInvesting'
      ]
    };
  }
}

const redditService = new RedditService();
export default redditService;
